/**
 * GaugeWidget — Semicircular dial for bounded-score metrics (Story 35.X bugfix).
 *
 * Renders a single metric as a 180° SVG gauge with a configurable target
 * (max value) and threshold-based color bands. Used by 12+ presets across
 * Ahrefs DR, Semrush Authority Score, Moz DA/PA/BA, CallRail/CTM Answer Rate,
 * Google/Trustpilot Reply Rate, Microsoft Ads Impression Share, etc.
 *
 * Consumes the same `KpiData` envelope shape as `KpiWidget` — server-side
 * `transformMetricsToWidgetData` routes `gauge` through `transformKpi`.
 *
 * Threshold semantics: each entry's `value` is the UPPER BOUND of its color
 * band. The first threshold whose `value >= currentValue` wins. When
 * `thresholds` is missing or empty, the gauge renders with a neutral
 * `text-foreground` fill and no banding.
 *
 * Alternative threshold shape: `thresholds: number[]` (used by Google Reviews
 * + Trustpilot reply-rate presets) is normalised internally to the
 * `{value, color}` form using a default red/amber/green/dark-green ramp.
 */

import type { ReactNode } from "react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { KpiData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isKpiData } from "../../../lib/widgetData";
import { formatKpiValue } from "@provenleap/types/report-rendering";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

// ── Types ────────────────────────────────────────────────────────────

type ThresholdColor = "red" | "amber" | "green" | "dark-green" | string;

interface GaugeThreshold {
  value: number;
  color: ThresholdColor;
}

interface GaugeConfig {
  metricKey?: string;
  target?: number;
  /** Alias for `target`, used by Google Reviews + Trustpilot presets. */
  max?: number;
  /** Alias for lower bound (default 0), used by review-platform presets. */
  min?: number;
  /** Object form (preferred): `[{ value: 30, color: "red" }, …]`. */
  thresholds?: GaugeThreshold[] | number[];
  format?: KpiData["format"];
  unit?: string;
}

interface GaugeWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<KpiData>;
}

// ── Color mapping ────────────────────────────────────────────────────

const COLOR_MAP: Record<string, string> = {
  red: "#dc2626",
  amber: "#f59e0b",
  green: "#16a34a",
  "dark-green": "#15803d",
  blue: "#2563eb",
  gray: "#6b7280",
};

function resolveColor(color: ThresholdColor | undefined): string {
  if (!color) return "#6b7280";
  return COLOR_MAP[color] ?? color;
}

/** Default threshold ramp for the legacy `number[]` shape. */
const DEFAULT_RAMP: ThresholdColor[] = ["red", "amber", "green", "dark-green"];

function normaliseThresholds(
  raw: GaugeConfig["thresholds"]
): GaugeThreshold[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  // Detect number[] form (used by Google Reviews + Trustpilot)
  if (typeof raw[0] === "number") {
    return (raw as number[]).map((value, idx) => ({
      value,
      color: DEFAULT_RAMP[Math.min(idx, DEFAULT_RAMP.length - 1)]!,
    }));
  }
  return raw as GaugeThreshold[];
}

/**
 * Pick the color for a current value by walking thresholds in order and
 * returning the first one whose `value >= currentValue`. Falls back to the
 * last threshold's color (or neutral) when value exceeds all bounds.
 */
function pickBandColor(
  value: number,
  thresholds: GaugeThreshold[]
): string {
  if (thresholds.length === 0) return "#6b7280";
  const sorted = [...thresholds].sort((a, b) => a.value - b.value);
  for (const t of sorted) {
    if (value <= t.value) return resolveColor(t.color);
  }
  return resolveColor(sorted[sorted.length - 1]!.color);
}

// ── SVG geometry ─────────────────────────────────────────────────────

/** Polar→cartesian for a 180° semicircle (angle 180°=left, 0°=right). */
function polarToCartesian(
  cx: number,
  cy: number,
  r: number,
  angleDeg: number
): { x: number; y: number } {
  const rad = ((angleDeg - 180) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** Build an SVG arc path from `startAngle` to `endAngle` (both 0…180°). */
function arcPath(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

// ── Component ────────────────────────────────────────────────────────

export function GaugeWidget({ widget, data }: GaugeWidgetProps) {
  const config = widget.config as GaugeConfig;
  const min = typeof config.min === "number" ? config.min : 0;
  const target = config.target ?? config.max ?? 100;
  const range = target - min;
  const thresholds = normaliseThresholds(config.thresholds);
  const { shell } = useChartWidgetShell(widget.config);

  const shellBody = (body: ReactNode) => shell("gauge", widget.title, body, { centered: true });

  // ── Loading state ───────────────────────────────────────────────
  if (data?.loading) {
    return shellBody(<div className="h-24 w-48 animate-pulse rounded-full bg-muted" />);
  }

  // ── Error state ─────────────────────────────────────────────────
  if (data?.error) {
    return shellBody(<ChartMessage role="alert">{data.error}</ChartMessage>);
  }

  // ── No-data state ───────────────────────────────────────────────
  const kpiData = data?.data;
  if (!kpiData || !isKpiData(kpiData)) {
    return shellBody(
      <span className="text-sm text-muted-foreground" data-testid="gauge-no-data">{"—"}</span>
    );
  }

  // ── Data state ──────────────────────────────────────────────────
  const rawValue = kpiData.value;
  // Clamp display value into [min, target] but preserve the actual number
  // for the text label so out-of-range data is still visible to the user.
  const clamped = Math.min(Math.max(rawValue, min), target);
  const pct = range > 0 ? (clamped - min) / range : 0;

  // Threshold lookup uses the un-clamped value so a 130 score still maps
  // to the "green" band if the highest threshold covers it.
  const bandColor = pickBandColor(rawValue, thresholds);

  // SVG dimensions
  const width = 240;
  const height = 130;
  const cx = width / 2;
  const cy = height - 10;
  const radius = 100;
  const strokeWidth = 18;

  // Build per-band background arcs when thresholds are present, otherwise
  // a single neutral track.
  const bandArcs: Array<{ d: string; color: string; key: string }> = [];
  if (thresholds.length > 0) {
    const sorted = [...thresholds].sort((a, b) => a.value - b.value);
    let prevValue = min;
    for (const t of sorted) {
      const startPct = range > 0 ? (prevValue - min) / range : 0;
      const endPct = range > 0 ? (Math.min(t.value, target) - min) / range : 0;
      if (endPct > startPct) {
        bandArcs.push({
          d: arcPath(cx, cy, radius, startPct * 180, endPct * 180),
          color: resolveColor(t.color),
          key: `band-${t.value}`,
        });
      }
      prevValue = t.value;
    }
  }

  // Filled arc for the current value
  const valueArc = arcPath(cx, cy, radius, 0, Math.max(pct * 180, 0.01));

  const formattedValue = formatKpiValue(rawValue, kpiData.format ?? config.format);
  const unitLabel = config.unit ?? (config.format === "percentage" ? "" : `/${target}`);

  const ariaLabel = `${widget.title}: ${formattedValue}${unitLabel} of ${target}`;

  return shellBody(
    <div
      className="flex w-full flex-col items-center justify-center"
      aria-label={ariaLabel}
      data-testid="gauge-content"
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-[240px]"
        role="img"
        aria-hidden="true"
        data-testid="gauge-svg"
      >
        {/* Background track */}
        {bandArcs.length === 0 ? (
          <path
            d={arcPath(cx, cy, radius, 0, 180)}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
          />
        ) : (
          bandArcs.map((arc) => (
            <path
              key={arc.key}
              d={arc.d}
              stroke={arc.color}
              strokeOpacity={0.25}
              strokeWidth={strokeWidth}
              fill="none"
            />
          ))
        )}
        {/* Value fill */}
        <path
          data-testid="gauge-value-arc"
          d={valueArc}
          stroke={bandColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
        />
      </svg>
      <div className="-mt-6 flex items-baseline gap-1">
        <span
          className="font-semibold text-foreground"
          style={{ fontSize: "28px", lineHeight: 1.1, letterSpacing: "-0.01em", color: bandColor }}
          data-testid="gauge-value"
        >
          {formattedValue}
        </span>
        {unitLabel && (
          <span
            className="text-sm text-muted-foreground"
            data-testid="gauge-unit"
          >
            {unitLabel}
          </span>
        )}
      </div>
    </div>,
  );
}
